import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Stack, createStyles} from '@mantine/core';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import type {AgentTypeError} from 'llm/chat-library/ChatDraftState';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import NoModelsInstalledEmptyState from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {
  getMaxAminoAcidCount,
  getMaxBulkPredictionItems,
  getMaxMultimerComponents,
  getMinAminoAcidCount,
  type ScientificModelNode,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import notifications from 'notifications';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as net from 'util/Net';

import InputsSection from './InputsSection';
import ModelSection from './ModelSection';
import {
  getSubmitDisabledReason,
  type PredictionInput,
  type PredictionInputsUpdate,
  normalizeInputsForModel,
} from './newPredictionState';
import {buildPredictionConfigs, runNewPredictionSubmit} from './newPredictionSubmit';
import NewPredictionTopBar from './NewPredictionTopBar';

interface NewPredictionPageProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  onBack: () => void;
  onSubmitted: () => void;
  preselectedModel?: ScientificModelNode;
}

const DEFAULT_AUTO_NAME = 'New prediction batch';

const CONTENT_MAX_WIDTH_PX = 1160;

export default function NewPredictionPage({
  availableModels,
  onBack,
  onSubmitted,
  preselectedModel,
}: NewPredictionPageProps): JSX.Element {
  const {classes} = useStyles();
  const [selectedModel, setSelectedModel] = useState<ScientificModelNode | null>(null);
  const [msaEnabled, setMSAEnabled] = useState(true);
  const [batchName, setBatchName] = useState('');
  const [autoName, setAutoName] = useState(DEFAULT_AUTO_NAME);
  // A name the user typed is theirs to keep; an untouched field keeps tracking the selected model.
  // Clearing the field hands the name back to the model-generated default.
  const isBatchNameUserEditedRef = useRef(false);
  // Start empty so the upload drop zone is the first thing shown; the user adds inputs by uploading
  // a CSV or choosing "Enter data".
  const [inputs, setInputs] = useState<Array<PredictionInput>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<AgentTypeError | null>(null);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_OPENED);
  }, []);

  useEffect(() => {
    if (availableModels.length === 0) {
      return;
    }
    // Only preselect a model when one was explicitly requested (e.g. a per-model "New prediction"
    // card). The generic entry starts empty so the user adds a model via the "+".
    if (
      preselectedModel == null ||
      !availableModels.some(
        (availableModel) => availableModel.externalReference === preselectedModel.externalReference
      )
    ) {
      return;
    }
    setSelectedModel((currentModel) => currentModel ?? preselectedModel);
  }, [availableModels, preselectedModel]);

  useEffect(() => {
    if (selectedModel == null) {
      return;
    }
    setMSAEnabled(supportsMSA(selectedModel));
    setInputs((currentInputs) => normalizeInputsForModel(selectedModel, currentInputs));
  }, [selectedModel]);

  useEffect(() => {
    setAutoName(DEFAULT_AUTO_NAME);
    if (!isBatchNameUserEditedRef.current) {
      // Drop the previous model's generated name so a stale one can't be submitted if the request
      // for the new model fails.
      setBatchName('');
    }
    if (selectedModel == null) {
      return;
    }

    let cancelled = false;
    net
      .postJSONPromise({
        url: '/1/api/model-hub/prediction-batch:generate-name',
        data: {model: selectedModel.externalReference},
      })
      .then((resp: {defaultName: string}) => {
        if (!cancelled) {
          setAutoName(resp.defaultName);
          if (!isBatchNameUserEditedRef.current) {
            setBatchName(resp.defaultName);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedModel]);

  const disabledReason = useMemo(
    () =>
      getSubmitDisabledReason({
        inputs,
        hasModels: availableModels.length > 0,
        hasSelectedModel: selectedModel != null,
        isSubmitting,
        maxAminoAcidCount: selectedModel == null ? 1 : getMaxAminoAcidCount(selectedModel),
        maxComponentsPerInput: selectedModel == null ? 1 : getMaxMultimerComponents(selectedModel),
        maxInputs: selectedModel == null ? 0 : getMaxBulkPredictionItems(selectedModel),
        minAminoAcidCount: selectedModel == null ? 1 : getMinAminoAcidCount(selectedModel),
        modelName: selectedModel?.displayName ?? '',
        moleculesSupported: selectedModel?.capabilities.supportsMultimerMolecules ?? false,
      }),
    [availableModels, inputs, isSubmitting, selectedModel]
  );

  const predictionCount = useMemo(
    () => (selectedModel == null ? 0 : buildPredictionConfigs({inputs, model: selectedModel}).length),
    [inputs, selectedModel]
  );

  // Any edit invalidates a prior submit failure, so dismiss stale banners on the next change.
  const clearSubmitErrors = (): void => {
    setError(null);
    setCreditError(null);
  };

  const handleModelChange = (model: ScientificModelNode | null): void => {
    clearSubmitErrors();
    if (model != null) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_MODEL_ADDED, {
        model: model.externalReference,
      });
    }
    setSelectedModel(model);
  };

  const handleBatchNameChange = (name: string): void => {
    isBatchNameUserEditedRef.current = name.trim() !== '';
    setBatchName(name);
  };

  const handleInputsChange = (nextInputs: PredictionInputsUpdate): void => {
    clearSubmitErrors();
    setInputs(nextInputs);
  };

  const handleRemoveModel = (): void => {
    clearSubmitErrors();
    if (selectedModel != null) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_MODEL_REMOVED, {
        model: selectedModel.externalReference,
      });
    }
    setSelectedModel(null);
  };

  const handleSubmit = async (): Promise<void> => {
    if (disabledReason != null || selectedModel == null) {
      return;
    }

    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_CLICKED, {
      model: selectedModel.externalReference,
      predictionCount,
    });
    setIsSubmitting(true);
    setError(null);
    setCreditError(null);
    try {
      await runNewPredictionSubmit({
        inputs,
        batchName: batchName.trim() || autoName,
        model: selectedModel,
        msaEnabled,
      });
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_SUCCEEDED, {
        model: selectedModel.externalReference,
        predictionCount,
      });
      notifications.success(`${selectedModel.displayName} batch created.`);
      onSubmitted();
    } catch (submitError) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_SUBMIT_FAILED, {
        model: selectedModel.externalReference,
      });
      const creditsError = getInsufficientCreditsAgentTypeError(submitError);
      if (creditsError) {
        setCreditError(creditsError);
        return;
      }
      const submitErrorMessage = getKnownSubmitErrorMessage(submitError);
      if (submitErrorMessage != null) {
        setError(submitErrorMessage);
        return;
      }
      if (submitError instanceof Error) {
        setError(submitError.message);
        return;
      }
      throw submitError;
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = (): JSX.Element => {
    if (availableModels.length === 0) {
      return <NoModelsInstalledEmptyState />;
    }

    return (
      <Stack spacing="lg">
        <ModelSection
          availableModels={availableModels}
          msaEnabled={msaEnabled}
          onMSAEnabledChange={setMSAEnabled}
          onModelChange={handleModelChange}
          onRemoveModel={handleRemoveModel}
          selectedModel={selectedModel}
        />
        <InputsSection
          disabled={selectedModel == null}
          inputs={inputs}
          model={selectedModel}
          onInputsChange={handleInputsChange}
        />
      </Stack>
    );
  };

  return (
    <div className={classes.root} {...getTestProps({component: 'NewPredictionPage', element: 'root'})}>
      <div className={classes.header}>
        <NewPredictionTopBar
          autoName={autoName}
          disabledReason={disabledReason}
          isSubmitting={isSubmitting}
          batchName={batchName}
          onBack={onBack}
          onBatchNameChange={handleBatchNameChange}
          onSubmit={handleSubmit}
          predictionCount={predictionCount}
        />
      </div>
      <div className={classes.body}>
        <div className={classes.bodyInner}>
          <Stack spacing="lg">
            {creditError && (
              <Alert
                icon={<BenchlingIcon icon="exclamation-triangle" />}
                onClose={() => {
                  setCreditError(null);
                }}
                title={creditError.title}
                variant="attention"
                withCloseButton={true}
              >
                {creditError.detail}
              </Alert>
            )}
            {error && (
              <Alert
                icon={<BenchlingIcon icon="exclamation-circle" />}
                onClose={() => {
                  setError(null);
                }}
                title="Batch couldn't be created"
                variant="error"
                withCloseButton={true}
              >
                {error}
              </Alert>
            )}
            {renderContent()}
          </Stack>
        </div>
      </div>
    </div>
  );
}

function getKnownSubmitErrorMessage(submitError: unknown): string | null {
  const userMessage = net.getUserMessage(submitError) ?? getStructuredUserMessage(submitError);
  if (userMessage != null) {
    return userMessage;
  }
  return null;
}

function getStructuredUserMessage(submitError: unknown): string | null {
  if (typeof submitError !== 'object' || submitError == null) {
    return null;
  }
  const responseJSON = (submitError as {responseJSON?: unknown}).responseJSON;
  if (typeof responseJSON === 'object' && responseJSON != null) {
    const userMessage = (responseJSON as {userMessage?: unknown}).userMessage;
    return typeof userMessage === 'string' ? userMessage : null;
  }
  const userMessage = (submitError as {userMessage?: unknown}).userMessage;
  return typeof userMessage === 'string' ? userMessage : null;
}

const useStyles = createStyles((theme) => ({
  root: {
    backgroundColor: theme.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  header: {
    backgroundColor: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    flex: '0 0 auto',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
  },
  body: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  bodyInner: {
    margin: '0 auto',
    maxWidth: CONTENT_MAX_WIDTH_PX,
    padding: `${theme.spacing.xl} ${theme.spacing.xl} 64px`,
  },
}));
